import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding, ThemePreset, ThemeMode } from '../../contexts/BrandingContext';
import { useToast } from '../../contexts/ToastContext';

interface PresetCard {
  id: ThemePreset;
  label: string;
  descriptionKey: string;
  swatches: { primary: string; accent: string; surface: string };
}

const PRESETS: PresetCard[] = [
  {
    id: 'mediterranean',
    label: 'Mediterranean',
    descriptionKey: 'branding.presetMediterranean',
    swatches: { primary: '#1e3a8a', accent: '#93cbb6', surface: '#ffffff' },
  },
  {
    id: 'slate-pro',
    label: 'Slate Pro',
    descriptionKey: 'branding.presetSlatePro',
    swatches: { primary: '#0f172a', accent: '#38bdf8', surface: '#ffffff' },
  },
  {
    id: 'forest',
    label: 'Forest',
    descriptionKey: 'branding.presetForest',
    swatches: { primary: '#064e3b', accent: '#84cc16', surface: '#ffffff' },
  },
  {
    id: 'plum',
    label: 'Plum',
    descriptionKey: 'branding.presetPlum',
    swatches: { primary: '#4c1d95', accent: '#e879f9', surface: '#ffffff' },
  },
];

const MODES: { id: ThemeMode; labelKey: string; hintKey: string }[] = [
  { id: 'light', labelKey: 'branding.modeLight', hintKey: 'branding.modeLightHint' },
  { id: 'dark', labelKey: 'branding.modeDark', hintKey: 'branding.modeDarkHint' },
  { id: 'auto', labelKey: 'branding.modeAuto', hintKey: 'branding.modeAutoHint' },
];

const BrandingSettings: React.FC = () => {
  const { t } = useTranslation();
  const branding = useBranding();
  const { addToast } = useToast();
  const [name, setName] = useState(branding.practice_name);
  const [tagline, setTagline] = useState(branding.header_tagline);
  const [savingName, setSavingName] = useState(false);
  const [savingTagline, setSavingTagline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(branding.practice_name);
  }, [branding.practice_name]);

  useEffect(() => {
    setTagline(branding.header_tagline);
  }, [branding.header_tagline]);

  const handleSaveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) return addToast(t('branding.errorNameEmpty'), 'error');
    if (trimmed === branding.practice_name) return;
    setSavingName(true);
    try {
      await branding.update({ practice_name: trimmed });
      addToast(t('branding.nameSaved'), 'success');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorSave'), 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveTagline = async () => {
    const trimmed = tagline.trim();
    if (trimmed === branding.header_tagline) return;
    setSavingTagline(true);
    try {
      await branding.update({ header_tagline: trimmed });
      addToast(t('branding.taglineSaved'), 'success');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorSave'), 'error');
    } finally {
      setSavingTagline(false);
    }
  };

  const handlePreset = async (preset: ThemePreset) => {
    if (preset === branding.theme_preset || busy) return;
    setBusy(true);
    try {
      await branding.update({ theme_preset: preset });
      addToast(t('branding.themeApplied', { preset }), 'success');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorThemeChange'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleMode = async (mode: ThemeMode) => {
    if (mode === branding.theme_mode || busy) return;
    setBusy(true);
    try {
      await branding.update({ theme_mode: mode });
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorModeChange'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 500 * 1024) return addToast(t('branding.errorLogoSize'), 'error');
    setBusy(true);
    try {
      await branding.uploadLogo(file);
      addToast(t('branding.logoUploaded'), 'success');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorUpload'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!branding.logo_url) return;
    setBusy(true);
    try {
      await branding.removeLogo();
      addToast(t('branding.logoRemoved'), 'success');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('branding.errorRemove'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg shadow p-6 space-y-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">🏷️ {t('branding.identityTitle')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('branding.identityDescription')}
          </p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('branding.practiceName')}</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-brand-primary/40 outline-none"
              placeholder="SeaDent"
            />
            <button
              onClick={handleSaveName}
              disabled={savingName || name.trim() === branding.practice_name}
              className="px-4 py-2 bg-brand-primary text-white rounded text-sm font-semibold disabled:opacity-50"
            >
              {savingName ? t('branding.saving') : t('common.save')}
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">{t('branding.practiceNameHint')}</p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('branding.headerText')}</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              maxLength={60}
              className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-brand-primary/40 outline-none"
              placeholder="Appointment Manager"
            />
            <button
              onClick={handleSaveTagline}
              disabled={savingTagline || tagline.trim() === branding.header_tagline}
              className="px-4 py-2 bg-brand-primary text-white rounded text-sm font-semibold disabled:opacity-50"
            >
              {savingTagline ? t('branding.saving') : t('common.save')}
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">{t('branding.headerTextHint')}</p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('branding.logo')}</label>
          <div className="flex items-start gap-4">
            <div
              className={`flex-1 border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
                dragOver ? 'border-brand-primary bg-brand-primary/5' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] || undefined)}
              />
              <p className="text-sm text-gray-700">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="text-brand-primary font-semibold hover:underline"
                  disabled={busy}
                >
                  {t('branding.chooseFile')}
                </button>{' '}
                {t('branding.orDragHere')}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">{t('branding.logoFormats')}</p>
            </div>

            <div className="w-32 h-32 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt={t('branding.currentLogoAlt')} className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[11px] text-gray-400">{t('branding.noLogo')}</span>
              )}
            </div>
          </div>
          {branding.logo_url && (
            <button
              onClick={handleRemoveLogo}
              disabled={busy}
              className="mt-2 text-xs text-red-600 hover:underline disabled:opacity-50"
            >
              {t('branding.removeLogo')}
            </button>
          )}
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6 space-y-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">🎨 {t('branding.colorSchemeTitle')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('branding.colorSchemeDescription')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRESETS.map((p) => {
            const active = p.id === branding.theme_preset;
            return (
              <button
                key={p.id}
                onClick={() => handlePreset(p.id)}
                disabled={busy}
                className={`text-left rounded-lg border-2 p-4 transition-all ${
                  active
                    ? 'border-brand-primary bg-brand-primary/5 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                } disabled:opacity-50`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900 text-sm">{p.label}</span>
                  {active && <span className="text-[10px] uppercase tracking-wider text-brand-primary font-bold">{t('branding.activeBadge')}</span>}
                </div>
                <p className="text-xs text-gray-500 mb-3">{t(p.descriptionKey)}</p>
                <div className="flex gap-1.5">
                  <span className="h-6 w-6 rounded-md border border-black/10" style={{ backgroundColor: p.swatches.primary }} />
                  <span className="h-6 w-6 rounded-md border border-black/10" style={{ backgroundColor: p.swatches.accent }} />
                  <span className="h-6 w-6 rounded-md border border-black/10" style={{ backgroundColor: p.swatches.surface }} />
                </div>
              </button>
            );
          })}
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{t('branding.mode')}</label>
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            {MODES.map((m) => {
              const active = m.id === branding.theme_mode;
              return (
                <button
                  key={m.id}
                  onClick={() => handleMode(m.id)}
                  disabled={busy}
                  title={t(m.hintKey)}
                  className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    active ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-600 hover:text-gray-900'
                  } disabled:opacity-50`}
                >
                  {t(m.labelKey)}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            {t('branding.modeAutoNote')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default BrandingSettings;
